import { api } from '../lib/api';
import { formatAgo, formatCount, formatDateTime } from '../lib/format';
import { href } from '../lib/route';
import { useLoad } from '../lib/useLoad';
import { AccountLink, Crumb, Fact, Loading, Pill, YesNo } from '../components/bits';
import { PhotoGrid } from '../components/PhotoGrid';
import { PageHead, Section } from '../components/Shell';

interface Props {
  readonly token: string;
  readonly id: string;
}

export const Frame = ({ token, id }: Props) => {
  const loaded = useLoad(() => api.getDevice(token, id), [token, id]);

  return (
    <>
      <Crumb label="Frames" to="/frames" />
      <Loading loaded={loaded}>
        {(device) => {
          const now = Date.now();
          const s = device.settings;
          return (
            <div className="stack-loose">
              <PageHead
                eyebrow="Frame"
                title={device.label}
                lede={
                  <>
                    Owned by <AccountLink account={device.owner} />
                    {device.publicUpload && <Pill tone="wait">public link</Pill>}
                    {device.autoAcceptEveryone && <Pill tone="wait">auto-accept everyone</Pill>}
                  </>
                }
              />

              <Section title="Details">
                <dl className="facts">
                  <Fact label="Created">{formatDateTime(device.createdAt)}</Fact>
                  <Fact label="Photos">
                    {formatCount(device.photoCount)} on the frame · {formatCount(device.pendingCount)} pending ·{' '}
                    {formatCount(device.deletedCount)} taken down
                  </Fact>
                  <Fact label="Anyone with the link may send">
                    <YesNo value={device.publicUpload} />
                    {device.hasPublicLink ? ' · a link has been minted' : ' · no link minted yet'}
                  </Fact>
                  <Fact label="Everything skips the queue">
                    <YesNo value={device.autoAcceptEveryone} />
                  </Fact>
                  <Fact label="Playlist version">
                    <span className="mono">{device.photosVersion}</span>
                  </Fact>
                  <Fact label="Id">
                    <span className="mono">{device.id}</span>
                  </Fact>
                </dl>
              </Section>

              <Section title="Slideshow settings">
                <dl className="facts">
                  <Fact label="Dwell">{s.dwellSeconds} s</Fact>
                  <Fact label="Fade">{s.fadeMs} ms</Fact>
                  <Fact label="Shuffle">
                    <YesNo value={s.shuffle} />
                  </Fact>
                  <Fact label="Ken Burns">
                    <YesNo value={s.kenBurns} />
                  </Fact>
                  <Fact label="Captions">
                    <YesNo value={s.showCaption} />
                  </Fact>
                  <Fact label="Quiet hours">
                    {s.nightFromHour === s.nightToHour
                      ? 'off'
                      : `${s.nightFromHour}:00 → ${s.nightToHour}:00 at ${Math.round(s.nightDim * 100)}%`}
                  </Fact>
                  <Fact label="Refresh">{s.refreshSeconds} s</Fact>
                </dl>
              </Section>

              <Section title={`People (${device.members.length})`}>
                {device.members.length === 0 ? (
                  <p className="empty">Nobody but the owner.</p>
                ) : (
                  <ul className="ledger">
                    {device.members.map((m) => (
                      <li key={m.id}>
                        <div className="ledger-main">
                          <span className="ledger-name">
                            <AccountLink account={m} />
                            <Pill>{m.role}</Pill>
                          </span>
                          <span className="ledger-meta">
                            joined {formatAgo(m.joinedAt, now)} · send <YesNo value={m.canSend} /> · auto-accept{' '}
                            <YesNo value={m.autoAccept} />
                            {m.role === 'delegate' && ' (delegates always send and skip the queue)'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title={`Invite links (${device.invites.length})`}>
                {device.invites.length === 0 ? (
                  <p className="empty">None minted.</p>
                ) : (
                  <ul className="ledger">
                    {device.invites.map((i) => {
                      const dead = i.revokedAt !== null || (i.expiresAt !== null && i.expiresAt < now);
                      return (
                        <li key={i.id}>
                          <div className="ledger-main">
                            <span className="ledger-name">
                              {i.label}
                              <Pill>{i.role}</Pill>
                              {dead && <Pill tone="bad">{i.revokedAt !== null ? 'withdrawn' : 'expired'}</Pill>}
                            </span>
                            <span className="ledger-meta">
                              by <AccountLink account={i.createdBy} /> · {formatAgo(i.createdAt, now)} · used{' '}
                              {formatCount(i.usedCount)} times
                              {i.expiresAt !== null && <> · expires {formatDateTime(i.expiresAt)}</>}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Section>

              <Section title={`Screens (${device.screens.length})`}>
                {device.screens.length === 0 ? (
                  <p className="empty">Nothing paired.</p>
                ) : (
                  <ul className="ledger">
                    {device.screens.map((sc) => (
                      <li key={sc.id}>
                        <div className="ledger-main">
                          <span className="ledger-name">
                            {sc.label}
                            {sc.revokedAt !== null && <Pill tone="bad">disconnected</Pill>}
                          </span>
                          <span className="ledger-meta">
                            paired {formatAgo(sc.createdAt, now)} · last seen {formatAgo(sc.lastSeenAt, now)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title={`Albums on this frame (${device.publications.length})`}>
                {device.publications.length === 0 ? (
                  <p className="empty">None.</p>
                ) : (
                  <ul className="ledger">
                    {device.publications.map((pub) => (
                      <li key={`${pub.album.id}-${pub.createdAt}`}>
                        <div className="ledger-main">
                          <span className="ledger-name">
                            {pub.album.label}
                            {pub.revokedAt !== null && <Pill tone="bad">taken down</Pill>}
                          </span>
                          <span className="ledger-meta">
                            album of <AccountLink account={pub.albumOwner} /> · published by{' '}
                            <AccountLink account={pub.createdBy} /> {formatAgo(pub.createdAt, now)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section
                title="Recent photos"
                aside={<a href={href('/photos', { deviceId: device.id, filter: 'all' })}>All, including taken down →</a>}
              >
                <PhotoGrid token={token} photos={device.recentPhotos} onChanged={loaded.reload} />
              </Section>
            </div>
          );
        }}
      </Loading>
    </>
  );
};
